import { Documents } from '@/modules/Documents/types/reponse'
import { Holiday } from '@/modules/Holiday/types/response'
import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import queryString from 'query-string'
import {
    BusinessTypes,
    CreateQuotePayload,
    CreateRevenueTargetPayload,
    DashboardAnalytics,
    GetDashboardParams,
    GetLeadQuickViewParams,
    GetLeadStatisticsParams,
    GetMonthlyRevenueParams,
    GetQuotesParams,
    GetRevenueTargetsParams,
    GetRevenueWithActualsParams,
    GetTechnicalTicketsParams,
    GetUnassignedTicketsParams,
    InterviewSchedule,
    LateEmployee,
    LeadQuickView,
    LeadStatistics,
    LeadStatisticsByWebsite,
    MonthlyRevenue,
    MyRecentWorkday,
    NotificationStatisticDashboard,
    ProcessedRevenueData,
    Quote,
    RandomQuoteResponse,
    RevenueResponseData,
    RevenueTarget,
    SaleTask,
    TechnicalTicket,
    TechnicalTicketSummary,
    UpdateRevenueTargetPayload,
} from '../types'
// Revenue Targets API
export const createRevenueTarget = async (
    payload: CreateRevenueTargetPayload
) => {
    const response = await datacentral.post<ResponseObject<RevenueTarget>>(
        '/revenue-targets/',
        payload
    )
    return response.data.result
}

export const getRevenueTarget = async (id: string) => {
    const response = await datacentral.get<ResponseObject<RevenueTarget>>(
        `/revenue-targets/${id}`
    )
    return response.data.result
}

export const getRevenueTargets = async (
    params: GetRevenueTargetsParams = {}
) => {
    const response = await datacentral.get<ResponseArray<RevenueTarget>>(
        `/revenue-targets?${queryString.stringify(params)}`
    )
    return response.data
}

export const updateRevenueTarget = async (
    payload: UpdateRevenueTargetPayload
) => {
    const { id, ...updateData } = payload
    const response = await datacentral.put<ResponseObject<RevenueTarget>>(
        `/revenue-targets/${id}`,
        updateData
    )
    return response.data.result
}

export const deleteRevenueTarget = async (id: string) => {
    const response = await datacentral.delete<
        ResponseObject<{ success: boolean }>
    >(`/revenue-targets/${id}`)
    return response.data.result
}

// Dashboard API - Updated to use new endpoint and params
export const getDashboardData = async (params: GetDashboardParams = {}) => {
    const response = await datacentral.get<ResponseObject<DashboardAnalytics>>(
        `/dashboard?${queryString.stringify(params)}`
    )
    return response.data.result
}

// Utility function to convert string numbers to billions
const convertToBillion = (stringValue: string): number => {
    if (!stringValue || stringValue.trim() === '') {
        return 0
    }

    const numValue = parseFloat(stringValue)

    // Check if parseFloat returned a valid number
    if (isNaN(numValue) || !isFinite(numValue)) {
        return 0
    }

    return numValue / 1_000_000_000 // Convert to billions
}

// Utility function to process revenue response data
export const processRevenueData = (
    data: RevenueResponseData,
    period: '3_months' | '6_months' | '12_months' = '6_months'
): ProcessedRevenueData => {
    console.log('DEBUG - Raw data:', data)

    // Get totals from monthly_comparisons first, then fallback to summary if needed
    let totalActual = 0
    let totalTarget = 0
    let avgCompletionPercentage = 0

    // Use monthly_comparisons as primary data source
    if (data.monthly_comparison && data.monthly_comparison.length > 0) {
        console.log(
            'DEBUG - Using monthly_comparison:',
            data.monthly_comparison
        )

        totalActual = data.monthly_comparison.reduce((sum, month) => {
            const actual = convertToBillion(month.period_actual || '0')
            console.log(
                'DEBUG - Month actual:',
                month.period_actual,
                '-> billion:',
                actual
            )
            return sum + actual
        }, 0)

        totalTarget = data.monthly_comparison.reduce((sum, month) => {
            const target = convertToBillion(month.period_target || '0')
            console.log(
                'DEBUG - Month target:',
                month.period_target,
                '-> billion:',
                target
            )
            return sum + target
        }, 0)

        // Calculate average completion percentage from monthly data
        avgCompletionPercentage =
            data.monthly_comparison?.reduce((sum, month) => {
                return sum + (month.period_completion_percentage || 0)
            }, 0) / data.monthly_comparison?.length

        console.log('DEBUG - Monthly totals:', {
            totalActual,
            totalTarget,
            avgCompletionPercentage,
        })
    }
    // Fallback to summary if monthly_comparisons is empty
    else if (data.summary) {
        console.log('DEBUG - Fallback to summary data:', data.summary)
        totalActual = convertToBillion(
            data.summary.total_actual_amount.toString() || '0'
        )
        totalTarget = convertToBillion(
            data.summary.total_target_amount.toString() || '0'
        )
        avgCompletionPercentage = data.summary.overall_completion || 0
        console.log('DEBUG - Summary totals:', {
            totalActual,
            totalTarget,
            avgCompletionPercentage,
        })
    }

    // Ensure we have valid numbers
    const safeActual = isNaN(totalActual) ? 0 : totalActual
    const safeTarget = isNaN(totalTarget) ? 0 : totalTarget

    const actualPercent = safeTarget > 0 ? (safeActual / safeTarget) * 100 : 0
    const targetPercent = 100 - actualPercent

    // Ensure percentages are valid numbers
    const safeActualPercent = isNaN(actualPercent) ? 0 : actualPercent
    const safeTargetPercent = isNaN(targetPercent) ? 0 : targetPercent

    // Fix completion percentage - looks like it might be in basis points (divide by 10000) or percent*100 (divide by 100)
    const completionPercentage = avgCompletionPercentage
        ? avgCompletionPercentage / 100
        : 0

    console.log('DEBUG - Final calculations:', {
        safeActual,
        safeTarget,
        safeActualPercent,
        safeTargetPercent,
        completionPercentage,
    })

    const periodLabel = {
        '3_months': 'dashboard.target-revenue.period-3-months',
        '6_months': 'dashboard.target-revenue.period-6-months',
        '12_months': 'dashboard.target-revenue.period-12-months',
    }[period]

    const result = {
        period: periodLabel,
        actual: Math.round(safeActual * 100) / 100, // Round to 2 decimal places
        target: Math.round(safeTarget * 100) / 100,
        unit: 'dashboard.target-revenue.unit-billion',
        actual_percent: Math.round(safeActualPercent * 100) / 100,
        target_percent: Math.round(safeTargetPercent * 100) / 100,
        completion_percentage: Math.round(completionPercentage * 100) / 100,
    }

    console.log('DEBUG - Final result:', result)
    return result
}

// Revenue with Actuals API - Updated to use new response structure
export const getRevenueTargetsWithActuals = async (
    params: GetRevenueWithActualsParams = {}
) => {
    // Convert period-based params to month range params
    const dashboardParams: GetRevenueWithActualsParams = {
        gte: params.gte,
        lte: params.lte,
    }

    return await datacentral.get<ResponseObject<RevenueResponseData>>(
        `/dashboard?${queryString.stringify(dashboardParams)}`
    )
}

// Monthly Revenue API
export const getMonthlyRevenue = async (params: GetMonthlyRevenueParams) => {
    const response = await datacentral.get<ResponseArray<MonthlyRevenue>>(
        `/api/revenue/monthly?${queryString.stringify(params)}`
    )
    return response.data
}

// Dashboard Analytics API - Updated
export const getDashboardAnalytics = async (
    params: GetRevenueWithActualsParams = {}
) => {
    // Convert period-based params to month range params
    const dashboardParams: GetDashboardParams = {
        gte: params.gte,
        lte: params.lte,
    }

    return getDashboardData(dashboardParams)
}

// Utility function to calculate comparison data
export const calculateRevenueComparison = (actual: number, target: number) => {
    const actualPercent = ((actual / target) * 100).toFixed(2)
    const targetPercent = (100 - parseFloat(actualPercent)).toFixed(2)

    return {
        actual_percent: parseFloat(actualPercent),
        target_percent: parseFloat(targetPercent),
    }
}

// get holidays -> workdays
export const getWorkDays = async (year: number, month: number) => {
    const response = await datacentral.get<ResponseArray<Holiday>>(
        `/workdays/holiday/holiday-by-year-and-month?year=${year}&month=${month}`
    )
    return response.data
}
// get notifications by document type
export const getNotificationsByDocumentType = async (documentType: string) => {
    const response = await datacentral.get<ResponseArray<Documents>>(
        `/documents/document-by-type?document_type=${documentType}`
    )
    return response.data
}

export const getNotificationStatisticDashboard = async () => {
    const response = await datacentral.get<
        ResponseObject<NotificationStatisticDashboard>
    >(`/documents/statistical-dashboard`)
    return response.data
}

export const getBusinessAndDayoffs = async () => {
    const response = await datacentral.get<ResponseObject<BusinessTypes>>(
        `/dashboard/business_and_dayoffs`
    )
    return response.data
}

export const getSaleTask = async ({
    gte,
    lte,
}: {
    gte: string
    lte: string
}) => {
    const response = await datacentral.get<ResponseObject<SaleTask>>(
        `/dashboard/sale_summary?gte=${gte}&lte=${lte}`
    )
    return response.data
}

// Technical Dashboard APIs
export const getTechnicalTicketSummary = async () => {
    const response = await datacentral.get<
        ResponseObject<TechnicalTicketSummary>
    >('/tickets/technical/summary')
    return response.data
}

export const getTechnicalTickets = async (
    params: GetTechnicalTicketsParams
) => {
    const response = await datacentral.get<ResponseArray<TechnicalTicket>>(
        `/tickets/technical?${queryString.stringify(params)}`
    )
    return response.data
}

export const getUnassignedTickets = async (
    params: GetUnassignedTicketsParams = {}
) => {
    const response = await datacentral.get<ResponseArray<TechnicalTicket>>(
        `/tickets/unassigned?${queryString.stringify(params)}`
    )
    return response.data
}

// Lead Statistics APIs
export const getLeadStatistics = async (
    params: GetLeadStatisticsParams = {}
) => {
    const response = await datacentral.get<ResponseObject<LeadStatistics>>(
        `/contacts/statistic?${queryString.stringify(params)}`
    )
    return response.data
}

export const getLeadStatisticsByWebsite = async (
    params: GetLeadStatisticsParams = {}
) => {
    const response = await datacentral.get<
        ResponseArray<LeadStatisticsByWebsite>
    >(
        `/contacts/statistic/by-website-referrer?${queryString.stringify(params)}`
    )
    return response.data
}

export const getLeadQuickView = async (params: GetLeadQuickViewParams = {}) => {
    const response = await datacentral.get<ResponseObject<LeadQuickView>>(
        `/contacts/quick-view?${queryString.stringify(params)}`
    )
    return response.data
}

// Interview Schedule API
export const getInterviewSchedule = async () => {
    const response = await datacentral.get<ResponseArray<InterviewSchedule>>(
        '/cv/interview-schedule'
    )
    return response.data?.results || []
}

// Late Employees API
export const getLateEmployees = async (params: {
    gte: string
    lte: string
    employee_id?: string
    department_id?: string
}) => {
    const response = await datacentral.get<ResponseArray<LateEmployee>>(
        `/workdays/late-employees?${queryString.stringify(params)}`
    )
    return response.data?.results || []
}

// My Recent Workdays API
export const getMyRecentWorkdays = async (params?: {
    gte?: string
    lte?: string
}) => {
    const url = params
        ? `/workdays/my-recent?${queryString.stringify(params)}`
        : '/workdays/my-recent'
    const response = await datacentral.get<ResponseArray<MyRecentWorkday>>(url)
    return response.data?.results || []
}

// Quote of the Day APIs
export const getRandomQuote = async () => {
    const response =
        await datacentral.get<ResponseObject<RandomQuoteResponse>>(
            '/quotes/random'
        )
    return response.data.result
}

export const getQuotes = async (params: GetQuotesParams = {}) => {
    const response = await datacentral.get<ResponseArray<Quote>>(
        `/quotes?${queryString.stringify(params)}`
    )
    return response.data
}

export const createQuote = async (payload: CreateQuotePayload) => {
    const response = await datacentral.post<ResponseObject<Quote>>(
        '/quotes',
        payload
    )
    return response.data.result
}
